import { axiosGet, axiosPostPromise, axiosDeletePromise, ApiResponse, axiosPutPromise } from '../utils/apiHelpers';
import { endPoints } from '../localisation/ApiUrls.en';

export interface CreateCustomerRequest {
    type: string;
    name: string;
    description?: string;
    contact?: string;
    email?: string;
    phone?: string;
    accountNumber?: string;
    siteAttributes?: {
        address?: string;
        city?: string;
        state?: string;
        country?: string;
        zipCode?: string;
    };
}

export interface CreateSiteRequest {
    type: string;
    name: string;
    description?: string;
    contact?: string;
    email?: string;
    phone?: string;
    parentSiteId: string;
    siteAttributes?: {
        address?: string;
        city?: string;
        state?: string;
        country?: string;
        zipCode?: string;
    };
}

export interface AlertMasterData {
    alert_type: number;
    description: string;
    troubleshooting_steps: string[];
}

export interface ApiAlertLog {
    id: string;
    alert_info_id: string;
    data: {
        type: number;
        severity: number;
        is_active: boolean;
        active_time: number;
        resolved_time: number;
        properties: Array<{
            name: number;
            pressure_value: number | null;
            unit: number | null;
            bit_value: string | null;
        }>;
    };
    created_on: number;
    filter_id: string;
}

// CustomerService centralises customer/site related API calls.
export const CustomerService = {
    getCustomers: (): Promise<ApiResponse> => axiosGet(endPoints.siteCustomers),

    searchCustomers: (customerName: string, accountNumber: string): Promise<ApiResponse> =>
        axiosGet(endPoints.searchCustomers(customerName, accountNumber)),

    getCustomerDetail: (customerId: string | number): Promise<ApiResponse> =>
        axiosGet(endPoints.getCustomerDetail(customerId)),

    getCustomerSites: (customerId: string | number): Promise<ApiResponse> =>
        axiosGet(endPoints.getCustomerSites(customerId)),

    getSiteDetail: (siteId: string | number): Promise<ApiResponse> =>
        axiosGet(endPoints.getSiteDetail(siteId)),

    getDistributorOrganizations: (): Promise<ApiResponse> =>
        axiosGet(endPoints.getDistributorOrganizations),

    searchSites: (customerId: string | number, payload: { name: string; city: string; state: string }): Promise<ApiResponse> =>
        axiosPostPromise(endPoints.searchSites(customerId), payload, false),

    registerCustomer: (payload: CreateCustomerRequest): Promise<ApiResponse> =>
        axiosPostPromise(endPoints.siteRegistration, payload, false),

    updateRegisterCustomer: (id: string | number, payload: CreateCustomerRequest): Promise<ApiResponse> =>
        axiosPutPromise(endPoints.updateSiteRegistration(id), payload, false),

    addSite: (payload: CreateSiteRequest): Promise<ApiResponse> =>
        axiosPostPromise(endPoints.siteRegistration, payload, false),

    deleteCustomer: (customerId: string | number): Promise<ApiResponse> =>
        axiosDeletePromise(endPoints.deleteCustomer(customerId), false),

    deleteSite: (siteId: string | number): Promise<ApiResponse> =>
        axiosDeletePromise(endPoints.deleteSite(siteId), false),

    getFiltersBySiteId: (siteId: string): Promise<ApiResponse> =>
        axiosGet(endPoints.getFiltersBySiteId(siteId)),

    getFiltersByDistributorOrg: (orgId: string, pageNumber: number = 1, pageSize: number = 100): Promise<ApiResponse> =>
        axiosGet(endPoints.getFiltersByDistributorOrg(orgId, pageNumber, pageSize)),

    getFilterByFilterId: (filterId: string): Promise<ApiResponse> =>
        axiosGet(endPoints.getFilterByFilterId(filterId)),

    deactivateFilter: (filterId: string): Promise<ApiResponse> =>
        axiosPutPromise(endPoints.deactivateFilter(filterId), {}, false),

    reactivateFilter: (filterId: string): Promise<ApiResponse> =>
        axiosPutPromise(endPoints.reactivateFilter(filterId), {}, false),

    getUsersBySiteId: (siteId: string): Promise<ApiResponse> =>
        axiosGet(endPoints.getUserListBySite(siteId)),

    getRoles: (): Promise<ApiResponse> =>
        axiosGet(endPoints.allUserRoles),

    // Alert/Alarm related APIs
    getAlertsMasterData: (): Promise<ApiResponse> => axiosGet(endPoints.alertsMasterData),

    getAlarmLogs: (filterId: string, limit: number = 30): Promise<ApiResponse> =>
        axiosGet(endPoints.getAlarmLogs(filterId, limit)),

    getOperationalFilterLogs: (filterId: string): Promise<ApiResponse> =>
        axiosGet(endPoints.getOperationalFilterLogs(filterId)),

    getPressureStatistics: (filterId: string): Promise<ApiResponse> =>
        axiosGet(endPoints.getPressureStatistics(filterId)),

    inviteUser: (email: string, roleId: string, siteId: string): Promise<ApiResponse> =>
        axiosPostPromise(endPoints.inviteUser, { email, role_id: roleId, site_id: siteId }, false),

    getPendingInvites: (siteId: string): Promise<ApiResponse> =>
        axiosGet(endPoints.getPendingInvites(siteId)),

    updateUser: (payload: { first_name: string; last_name: string; user_id: string; phone_number: string; role_id: string; site_id: string }): Promise<ApiResponse> =>
        axiosPutPromise(endPoints.updateUser, payload, false),

    getUserRoles: (userId: string): Promise<ApiResponse> =>
        axiosGet(endPoints.getUserRoles(userId)),

    updateUserRole: (userId: string, payload: { role: { roleId: string; siteIds: string[] }[] }): Promise<ApiResponse> =>
        axiosPutPromise(endPoints.updateUserRole(userId), payload, false),

    getUsersByRoleId: (roleId: string): Promise<ApiResponse> =>
        axiosGet(endPoints.getUsersByRoleId(roleId)),

    updateFilterDetails: (payload: Record<string, any>): Promise<ApiResponse> =>
        axiosPutPromise(endPoints.updateFilterDetails, payload, false),

    getUserDetail: (userId: string): Promise<ApiResponse> =>
        axiosGet(endPoints.getUserDetail(userId)),

    deleteUserAccount: (userId: string): Promise<ApiResponse> =>
        axiosDeletePromise(endPoints.deleteUserAccount(userId), false),
};
